import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../api';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { ArrowLeft, TrendingUp, Calendar, IndianRupee, Award } from 'lucide-react';

const DriverEarnings = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [earnings, setEarnings] = useState({
    today: 0, week: 0, month: 0, total: 0,
    trips_today: 0, trips_week: 0, trips_month: 0,
  });

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await api.get('/api/driver/earnings');
        setEarnings(data);
      } catch { /* ignore */ }
    };
    load();
  }, []);

  const tripsMonth = earnings.trips_month || 0;
  const avgPerTrip = tripsMonth > 0 ? Math.round(earnings.month / tripsMonth) : 0;
  const avgPerDay = Math.round((earnings.week || 0) / 7);

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/driver/dashboard')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-2xl font-bold">Earnings</h1>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-6 max-w-4xl">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <IndianRupee className="w-5 h-5 text-green-600" />
              <p className="text-sm text-muted-foreground">Today</p>
            </div>
            <p className="text-2xl font-bold text-green-600" data-testid="earnings-today">₹{earnings.today}</p>
            <p className="text-xs text-muted-foreground mt-1">{earnings.trips_today} trips</p>
          </Card>

          <Card className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="w-5 h-5 text-primary" />
              <p className="text-sm text-muted-foreground">This Week</p>
            </div>
            <p className="text-2xl font-bold text-primary">₹{earnings.week}</p>
            <p className="text-xs text-muted-foreground mt-1">{earnings.trips_week} trips</p>
          </Card>

          <Card className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-5 h-5 text-accent-dark" />
              <p className="text-sm text-muted-foreground">This Month</p>
            </div>
            <p className="text-2xl font-bold text-accent-dark">₹{earnings.month}</p>
            <p className="text-xs text-muted-foreground mt-1">{earnings.trips_month} trips</p>
          </Card>

          <Card className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Award className="w-5 h-5 text-yellow-600" />
              <p className="text-sm text-muted-foreground">Total</p>
            </div>
            <p className="text-2xl font-bold text-yellow-600">₹{earnings.total}</p>
            <p className="text-xs text-muted-foreground mt-1">All time</p>
          </Card>
        </div>

        <Card className="p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">Performance Insights</h2>
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center p-4 bg-background rounded-lg">
              <p className="text-3xl font-bold text-foreground">₹{avgPerTrip}</p>
              <p className="text-sm text-muted-foreground mt-1">Avg per trip</p>
            </div>
            <div className="text-center p-4 bg-background rounded-lg">
              <p className="text-3xl font-bold text-foreground">₹{avgPerDay}</p>
              <p className="text-sm text-muted-foreground mt-1">Avg per day</p>
            </div>
            <div className="text-center p-4 bg-background rounded-lg">
              <p className="text-3xl font-bold text-foreground">{(user?.rating ?? 5.0).toFixed(1)} ⭐</p>
              <p className="text-sm text-muted-foreground mt-1">Your rating</p>
            </div>
          </div>
        </Card>

        <Card className="p-6 bg-accent/10 border-accent/30">
          <div className="flex items-start gap-3">
            <IndianRupee className="w-6 h-6 text-primary mt-1" />
            <div>
              <h3 className="font-semibold text-primary mb-2">Earnings Payout</h3>
              <p className="text-sm text-primary">
                Earnings transferred to bank every Monday. Current week: ₹{earnings.week}
              </p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default DriverEarnings;
